import React, { useState } from 'react';
import { SEO } from '../components/SEO';
import { Container } from '../components/ui/Container';
import { PricingCard } from '../components/PricingCard';
import { FAQSection } from '../components/FAQSection';
import { Button } from '../components/ui/Button';
export function PricingPage() {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('annual');
  const isAnnual = billingPeriod === 'annual';
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Flowsense',
    description: 'AI-powered Klaviyo flow optimization platform',
    offers: [{
      '@type': 'Offer',
      name: 'Free Tier',
      price: '0',
      priceCurrency: 'USD'
    }, {
      '@type': 'Offer',
      name: 'Pro Tier',
      price: isAnnual ? '250' : '30',
      priceCurrency: 'USD',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: isAnnual ? '250' : '30',
        priceCurrency: 'USD',
        unitText: isAnnual ? 'YEAR' : 'MONTH'
      }
    }, {
      '@type': 'Offer',
      name: 'Agency',
      price: isAnnual ? '250' : '30',
      priceCurrency: 'USD',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: isAnnual ? '250' : '30',
        priceCurrency: 'USD',
        unitText: isAnnual ? 'YEAR' : 'MONTH'
      }
    }]
  };
  return <>
      <SEO title="Pricing - Flowsense" description="Simple, transparent pricing for Flowsense. Choose from Free, Pro, or Agency plans. Start free, upgrade anytime. No credit card required." canonical="https://www.useflowsense.com/pricing" structuredData={structuredData} data-id="element-1863" />
      <div className="bg-white" data-id="element-1864">
        {/* Hero */}
        <div className="bg-gray-900 py-20 lg:py-32 text-center text-white" data-id="element-1865">
          <Container data-id="element-1866">
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl mb-6" data-id="element-1867">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-10" data-id="element-1868">
              Choose the plan that fits your needs. Flexible monthly or annual
              billing.
            </p>

            {/* Billing Toggle */}
            <div className="flex justify-center items-center mt-8" data-id="element-1869">
              <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full p-1.5 space-x-1 border border-white/20" data-id="element-1870">
                <button onClick={() => setBillingPeriod('monthly')} className={`px-6 py-2.5 rounded-full text-base font-semibold transition-all ${!isAnnual ? 'bg-white text-gray-900 shadow-lg' : 'text-white hover:text-gray-200'}`} data-id="element-1871">
                  Monthly
                </button>
                <button onClick={() => setBillingPeriod('annual')} className={`px-6 py-2.5 rounded-full text-base font-semibold transition-all ${isAnnual ? 'bg-white text-gray-900 shadow-lg' : 'text-white hover:text-gray-200'}`} data-id="element-1872">
                  Annual
                  <span className="ml-2 text-green-400 font-bold text-sm" data-id="element-1873">
                    Save 30%
                  </span>
                </button>
              </div>
            </div>
          </Container>
        </div>

        {/* Pricing Cards */}
        <div className="-mt-20 pb-24" data-id="element-1874">
          <Container data-id="element-1875">
            <div className="grid md:grid-cols-3 gap-8 items-start" data-id="element-1876">
              <PricingCard name="Free Tier" price="$0" period={isAnnual ? '/year' : '/month'} description="Perfect for testing with one flow" cta="Try for Free" ctaLink="https://app.useflowsense.com/auth/signup" features={['1 flow analysis', 'Basic recommendations', '7-day performance tracking', 'Live chatbot support']} data-id="element-1877" />
              <PricingCard name="Pro Tier" price={isAnnual ? '$250' : '$30'} period={isAnnual ? '/year' : '/month'} description="Everything you need to optimize all flows" cta="Sign Up" ctaLink="https://app.useflowsense.com/auth/signup" highlighted={true} savings={isAnnual ? 'Save 30%' : undefined} features={['Unlimited flow analysis', 'Advanced AI recommendations', 'Full ROI tracking', 'Brand voice analysis', 'Flow error/failure email alerts', 'Live chatbot support', 'Export reports']} data-id="element-1878" />
              <PricingCard name="Agency" price={isAnnual ? '$250' : '$30'} period={isAnnual ? '/year' : '/month'} description={`For the first client, then ${isAnnual ? '$150/year' : '$20/month'} per additional client`} cta="Contact Sales" ctaLink="https://app.useflowsense.com/auth/signup" savings={isAnnual ? 'Save 30%' : undefined} features={['Everything in Pro', 'Unlimited client accounts', 'White-label reports', 'Custom domain', 'Team collaboration', 'Live chatbot support']} data-id="element-1879" />
            </div>
          </Container>
        </div>

        {/* FAQ */}
        <section className="py-24 bg-gray-50" data-id="element-1880">
          <Container data-id="element-1881">
            <div className="text-center mb-16" data-id="element-1882">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900" data-id="element-1883">
                Frequently Asked Questions
              </h2>
            </div>
            <FAQSection items={[{
            question: "What's included in the free tier?",
            answer: 'The free tier gives you access to analyze one flow with basic recommendations and 7-day performance tracking. Perfect for testing Flowsense before upgrading.'
          }, {
            question: 'Can I cancel anytime?',
            answer: 'Yes, you can cancel your subscription at any time. There are no long-term contracts or cancellation fees.'
          }, {
            question: 'Do you offer refunds?',
            answer: "We offer a 14-day money-back guarantee. If you're not satisfied with Flowsense, just let us know and we'll refund your payment."
          }, {
            question: 'How does agency pricing work?',
            answer: 'Agencies pay $250/year (or $30/month) for the first client. Additional clients are discounted to $150/year (or $20/month) each. All clients get full Pro features with white-label reporting.'
          }]} data-id="element-1884" />
          </Container>
        </section>

        {/* CTA */}
        <section className="py-24" data-id="element-1885">
          <Container data-id="element-1886">
            <div className="bg-gray-900 rounded-3xl overflow-hidden shadow-2xl text-center py-16 px-6 lg:py-24 lg:px-12" data-id="element-1887">
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl mb-6" data-id="element-1888">
                Ready to optimize your Klaviyo flows?
              </h2>
              <p className="text-xl text-gray-300 mb-10" data-id="element-1889">
                Start your free trial today and see how Flowsense can improve
                your email marketing performance.
              </p>
              <a href="https://go.useflowsense.com/signup" data-id="element-1890">
                <Button size="lg" data-id="element-1891">Sign Up</Button>
              </a>
            </div>
          </Container>
        </section>
      </div>
    </>;
}